import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'
import { Field, FieldProps, Formik } from 'formik'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TbLink } from 'react-icons/tb'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import {
  CreateBookmarkErrorResponse,
  CreateBookmarkSuccessResponse,
} from '../../interfaces'
import { UserContext } from '../../providers'

interface Props {
  isOpen: boolean
  onClose(): void
  getAndSetBookmarks(): void
}

interface FormProps {
  name: string
  link: string
}

const AddBookmarkModal = ({
  isOpen,
  onClose,
  getAndSetBookmarks,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [formError, setFormError] = useState<string>('')
  const { user } = useContext(UserContext)
  const toast = useToast()
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFormError('')
      }}
      size="xl"
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{
            name: '',
            link: '',
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().required('Required'),
            link: Yup.string()
              .matches(
                /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/,
                'Enter a valid URL',
              )
              .required('Required')
              .max(2000, 'Link cannot exceed 2000 characters.'),
          })}
          onSubmit={async values => {
            setFormError('')
            const res = (await request.post(
              `/api/media-library/${user?.clientNumber}/bookmarks`,
              values,
            )) as CreateBookmarkSuccessResponse | CreateBookmarkErrorResponse

            if (request.error) {
              setFormError(
                (res as CreateBookmarkErrorResponse).message ??
                  t(
                    'error_adding_bookmark',
                    'Error adding bookmark, please try again later.',
                  ),
              )
            } else {
              toast({
                description: t(
                  'bookmark_added_success',
                  'Bookmark added successfully.',
                ),
                status: 'success',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
              })
              setFormError('')
              void getAndSetBookmarks()
              onClose()
            }
          }}
        >
          {({ submitForm, errors, touched, isSubmitting, dirty, isValid }) => (
            <>
              <ModalHeader>
                <Icon
                  as={TbLink}
                  color="appBattleshipGrey"
                  backgroundColor="appPaleGrey"
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="full"
                  marginRight="0.5em"
                />
                {t('add_bookmark', 'Add bookmark')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {formError ? (
                  <Alert status="error" mb="14px">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <Field name="name">
                  {({ field }: FieldProps<string, FormProps>) => (
                    <FormControl
                      isRequired
                      isInvalid={Boolean(errors.name && touched.name)}
                      mb={4}
                    >
                      <FormLabel>{t('name', 'Name')}</FormLabel>
                      <Input
                        {...field}
                        placeholder={t(
                          'bookmark_name_placeholder',
                          'Enter bookmark name',
                        )}
                      />
                      {errors.name && touched.name ? (
                        <FormErrorMessage>{errors.name}</FormErrorMessage>
                      ) : null}
                    </FormControl>
                  )}
                </Field>
                <Field name="link">
                  {({ field }: FieldProps<string, FormProps>) => (
                    <FormControl
                      isRequired
                      isInvalid={Boolean(errors.link && touched.link)}
                    >
                      <FormLabel>{t('link', 'Link')}</FormLabel>
                      <Input
                        {...field}
                        placeholder={t(
                          'bookmark_link_placeholder',
                          'Enter bookmark URL',
                        )}
                      />
                      {errors.link && touched.link ? (
                        <FormErrorMessage>{errors.link}</FormErrorMessage>
                      ) : null}
                    </FormControl>
                  )}
                </Field>
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button variant="outline" onClick={onClose}>
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  colorScheme="appBlue"
                  onClick={async () => await submitForm()}
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting || !dirty || !isValid}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default AddBookmarkModal
